import {Spin} from "antd"
import React, {useEffect, useState} from "react"
import {calendarService} from "services"
import {CreateEvent, ListEvents} from "./parts"
import styles from "./StudentInfoCalendarPanel.module.css"
import {Auth} from "types/auth"
import {useQuery} from "@tanstack/react-query"
import {getFullName, handleError} from "helpers"
import {get, isEmpty} from "lodash"
import {useModel} from "hooks"

type DisplayPart = "list" | "create"

type Props = {
    displayPart?: DisplayPart
    departmentId?: number
    student?: Auth.DepartmentStudent
    onClose?: () => void
}

export const StudentInfoCalendarPanel = (props: Props) => {
    const {onClose, displayPart = "list", student} = props
    const [displayMode, setDisplayMode] = useState<DisplayPart>(displayPart)
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [calendarId, setCalendarId] = useState(null)
    const model = useModel()

    const {data: linkedCalendars} = useQuery(
        ["my-linked-calendars"],
        async () => {
            const data = await calendarService.getMyCalendars()
            const linkedCalendars = data
                .filter((item) => item.is_primary || !isEmpty(item.integration_data) || item.type === "group")
                .map((item) => ({
                    isPrimary: item.is_primary,
                    type: item.type,
                    id:
                        item.type === "group"
                            ? item.calendar_id
                            : !item.is_primary
                            ? get(item, "integration_data.id")
                            : null,
                    calendarId: item.calendar_id,
                    name: item.is_primary ? getFullName(model.user) : item.name,
                    provider: !item.is_primary ? get(item, "integration_data.account.provider") : null,
                    requiresReconnect: !item.is_primary && !!get(item, "integration_data.requiresReconnect"),
                    isShow: item.is_primary || !get(item, "integration_data.requiresReconnect")
                }))
            return linkedCalendars
        },
        {onError: (error) => handleError(error), initialData: []}
    )

    useEffect(() => {
        getData()
    }, [])

    const getData = async () => {
        const {data} = await calendarService.getMyPrimaryCalendar({}, {})
        const {calendar_id} = data
        setCalendarId(calendar_id)
    }

    const onNavigate = (_displayMode: DisplayPart) => {
        setDisplayMode(_displayMode)
    }

    if (!calendarId) {
        return (
            <div className={styles.loading}>
                <Spin />
            </div>
        )
    }

    return (
        <>
            {displayMode === "list" ? (
                <ListEvents
                    onNavigate={onNavigate}
                    onClose={onClose}
                    departmentId={props.departmentId}
                    student={student}
                    onSelectedDayChange={setSelectedDate}
                    selectedDay={selectedDate}
                />
            ) : (
                <CreateEvent
                    calendarId={calendarId}
                    student={student}
                    selectedDate={selectedDate}
                    linkedCalendars={linkedCalendars}
                    onNavigate={onNavigate}
                />
            )}
        </>
    )
}
